/**
 * The messages in the playfield (spec 08 §2), where the C64 put them: the title, ROUND nn / READY !
 * after the fly-in, HURRY UP !, PLAYER n GETS EXTEND, the bonus stage's clock, PAUSED, GAME OVER,
 * and the ending's lines.
 */

import type { BonusLook } from '../../sim/items';
import type { World } from '../../sim/types';
import { STORY_LINES, storyRowY } from '../../render/title';
import type { Banner } from '../../ui/hud/messages';

export interface BannerState {
  mode: 'title' | 'story' | 'play' | 'scroll' | 'over' | 'ending';
  paused: boolean;
  world: World | null;
  /** Frames since the mode began (for flashing). */
  timer: number;
  hurryShown: number;
  extendPlayer: number;
  suporr: boolean;
  /** Seconds left of the potion bonus stage, or -1; the stage itself, while on. */
  bonusSeconds: number;
  bonus: BonusLook | null;
  /** Reduce flashing (08 §4): messages hold one colour instead of alternating. */
  steady: boolean;
}

/**
 * The C64's story screen (08 §1): its four lines where it drew them, in its yellow, the font at the C64's
 * proportion (a character a playfield row). "FANTASIC" is the C64's own spelling.
 */
const STORY: Banner[] = STORY_LINES.map((l) => ({ lines: [l.text], colour: '#edf171', scale: 2, y: storyRowY(l.row) }));

export function banners(s: BannerState): Banner[] {
  const w = s.world;
  const flash = s.steady || Math.floor(s.timer / 8) % 2 === 0;
  if (s.mode === 'title') {
    // The menu draws the title's lines; the C64's SUPORR chord turned the border red (02 §9.5).
    return s.suporr ? [{ lines: ['SUPORR'], colour: '#ff3a6a', y: 312 }] : [];
  }
  if (s.mode === 'story') return STORY;
  if (s.mode === 'ending') {
    return [
      { lines: ['CONGRATULATIONS'], colour: '#edf171', scale: 2, y: 60 },
      { lines: ['YOU HAVE COMPLETED', '', 'BUBBLE BOBBLE'], colour: '#ffffff', y: 110 },
      { lines: ['! WHAT HEROES !'], colour: flash ? '#9be36a' : '#9cc4ff', scale: 2, y: 170 },
    ];
  }
  if (s.mode === 'over') return [{ lines: ['GAME OVER'], colour: '#ff3a6a', scale: 2, y: 160 }];
  if (s.paused) return [{ lines: ['PAUSED', '', 'P TO GO ON', 'Q TO QUIT'], colour: '#ffffff', y: 150 }];
  if (!w || s.mode !== 'play') return [];
  const out: Banner[] = [];
  const f = w.flow;
  if (f.phase !== 'flyIn' && f.frame < 100 && w.level.n > 0) {
    out.push({ lines: [`ROUND ${w.level.n}`, '', 'READY !'], colour: '#ffffff', y: 150 });
  }
  if (s.hurryShown >= 0 && f.frame - s.hurryShown < 100 && f.frame >= s.hurryShown) {
    out.push({ lines: ['HURRY UP !'], colour: flash ? '#edf171' : '#c68cff', scale: 2, y: 160 });
  }
  if (f.extendFrames > 0) out.push({ lines: [`PLAYER ${s.extendPlayer + 1} GETS EXTEND`], colour: '#ffb000', y: 170 });
  const b = s.bonus;
  if (b?.phase === 'results') {
    // The potion stage's tally: each player's count, and PERFECT when every item was taken.
    const lines = [`1UP  ${b.counts[0]}`, '', `2UP  ${b.counts[1]}`];
    out.push({ lines: b.perfect ? ['PERFECT !', '', ...lines] : lines, colour: flash ? '#edf171' : '#ffffff', y: 130 });
  } else if (s.bonusSeconds >= 0) {
    out.push({ lines: [`${s.bonusSeconds}`], colour: '#edf171', scale: 2, y: 12 });
  }
  return out;
}
